import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styles from './FAQSearchResult.module.scss';
import FAQSearchBar from './FAQSearchBar';
import FAQNavLeft from '../UI/FAQNavLeft/FAQNavLeft';
import FAQSearchResultCard from './FAQSearchResultCard';

import useFetchFAQAllQuestions from '../../hooks/useApi/useFetchFAQAllQuestions';
import useFetchFAQCategories from '../../hooks/useApi/useFetchFAQCategories';
import useDataFetchIndicator from '../../hooks/useDataFetchIndicator';
import { useTranslation } from 'react-i18next';

const { SubTitle } = vp;

type FAQCategoriesCards = {
  title: string;
  position: string;
  info: string[];
  logo: string;
};

type FC = FunctionComponent;

const FAQSearchResult: FC = () => {
  const location = useLocation();
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const searchParams = new URLSearchParams(location.search);
  const keyword = searchParams.get('keyword') || '';

  const [searchFiltered, setSearchFiltered] = useState<vp.FAQAllQuestions[]>([]);
  const [categories, fetchFAQCategories] = useFetchFAQCategories({});
  const [allQuestions, fetchFAQAllQuestions] = useFetchFAQAllQuestions({});
  const [faqCategories, setFaqCategories] = useState<FAQCategoriesCards[]>([]);
  const { t } = useTranslation('translations', { keyPrefix: 'LearningCentre.FAQ.SearchResult' });

  useEffect(() => {
    fetchFAQAllQuestions();
    fetchFAQCategories();
  }, []);

  useEffect(() => {
    if (allQuestions.Success) {
      const isEmpty = /^\s*$/.test(keyword);
      if (isEmpty) {
        setSearchFiltered([]);
        return;
      }
      setSearchFiltered(
        Object.values(allQuestions.FAQAllQuestions).filter(
          (data) =>
            data.Question.toLowerCase().includes(keyword.toLowerCase()) ||
            data.Answer.toLowerCase().includes(keyword.toLowerCase()),
        ),
      );
    }
  }, [keyword, allQuestions]);

  useEffect(() => {
    if (categories.Success) {
      Object.values(categories.FAQCategories).map((category) => {
        const convertedInfo = category.Description.split('|').map((item) => item.trim());
        setFaqCategories((prevCategory) => [
          ...prevCategory,
          {
            title: category.Category,
            position: category.Position,
            info: convertedInfo,
            logo: category.IconBase64,
          },
        ]);
      });
    }
  }, [categories]);

  useEffect(() => {
    if (allQuestions.Success && faqCategories.length !== 0) {
      setDataFetchingInfo(false, 'Loading...', 'fullScreen');
    } else {
      const interval = setInterval(() => {
        setDataFetchingInfo(false, 'Loading...', 'fullScreen');
        setDataFetchingInfo(true, 'Loading...', 'fullScreen');
      }, 150);
      return () => {
        clearInterval(interval);
      };
    }
    return () => {};
  }, [allQuestions, faqCategories]);

  return (
    <>
      <div className={styles.searchResult__container}>
        <div className={styles.searchResult__leftContainer}>
          <FAQNavLeft FAQCategories={faqCategories} />
        </div>
        <div className={styles.searchResult__rightContainer}>
          <div className={styles.searchBar__container}>
            <FAQSearchBar position="flex-end" borderColor="#C4C4C4" searchedValue={keyword} />
          </div>
          {allQuestions.Success && (
            <>
              <div className={styles.searchTitle__container}>
                <SubTitle subTitle="Search FAQ" fontSize="24px" />
                <p>{t('resultsCount', { count: searchFiltered.length, keyword })}</p>
                <div className={styles.yellowUnderlineBar} />
              </div>
              <div>
                {searchFiltered.length > 0 ? (
                  searchFiltered.map((data) => (
                    <FAQSearchResultCard
                      category={data.Category}
                      subCategory={data.SubCategory}
                      title={data.Question}
                      description={data.Answer}
                      keyword={keyword}
                    />
                  ))
                ) : (
                  <div>
                    <h4 style={{ fontWeight: 600, marginBottom: '16px', fontSize: '20px' }}>
                      {t('noResults', { keyword })}
                    </h4>
                    <p>{t('tryAnotherSearch')}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default FAQSearchResult;
